import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  ALL_NOTIFICATION_CATEGORIES,
  NOTIFICATION_CATEGORY_LABELS,
  useNotificationList,
} from '../features/notifications';
import { useMarketConfigs } from '../features/settings';
import type {
  AdminNotificationDTO,
  NotificationCategory,
} from '../features/notifications';

interface NotificationFormState {
  marketId: string;
  category: NotificationCategory;
  title: string;
  summary: string;
  body: string;
  actionLabel: string;
}

const EMPTY_FORM: NotificationFormState = {
  marketId: 'CN',
  category: 'system',
  title: '',
  summary: '',
  body: '',
  actionLabel: '',
};

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes(),
  ).padStart(2, '0')}`;
}

function mapForm(item: AdminNotificationDTO): NotificationFormState {
  return {
    category: item.category,
    marketId: item.market_id || 'CN',
    title: item.title,
    summary: item.summary,
    body: item.body,
    actionLabel: item.action_label ?? '',
  };
}

export function NotificationsPage() {
  const {
    items,
    total,
    page,
    totalPages,
    category,
    loading,
    saving,
    error,
    setPage,
    setCategory,
    marketId,
    setMarketId,
    reload,
    create,
    update,
    remove,
  } = useNotificationList();
  const { items: markets } = useMarketConfigs();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<NotificationFormState>(EMPTY_FORM);

  useEffect(() => {
    if (editingId === null) {
      return;
    }
    const current = items.find((item) => item.id === editingId);
    if (!current) {
      setEditingId(null);
      setForm(EMPTY_FORM);
    }
  }, [editingId, items]);

  const editingItem = useMemo(
    () => items.find((item) => item.id === editingId) ?? null,
    [editingId, items],
  );

  const startCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, marketId: marketId || 'CN' });
  };

  const startEdit = (item: AdminNotificationDTO) => {
    setEditingId(item.id);
    setForm(mapForm(item));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const payload = {
      category: form.category,
      market_id: form.marketId,
      title: form.title.trim(),
      summary: form.summary.trim(),
      body: form.body.trim(),
      action_label: form.actionLabel.trim(),
    };

    const ok =
      editingId === null
        ? await create(payload)
        : await update(editingId, payload);

    if (ok) {
      startCreate();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">消息通知</h1>
          <p className="mt-1 text-sm text-gray-500">
            管理移动端消息中心的真实通知数据，发布后会直接影响 App 的未读红点与消息列表。
          </p>
        </div>
        <button
          onClick={startCreate}
          className="px-4 py-2 rounded-md bg-primary text-white text-sm hover:bg-primary/90 transition-colors"
        >
          新建通知
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_380px] items-start">
        <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200">
            <div>
              <p className="text-sm font-medium text-gray-900">通知列表</p>
              <p className="text-xs text-gray-500 mt-1">共 {total} 条，按创建时间倒序展示。</p>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={marketId}
                onChange={(event) => setMarketId(event.target.value)}
                className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">全部地区</option>
                {markets.map((market) => (
                  <option key={market.market_id} value={market.market_id}>
                    {market.market_id} · {market.name}
                  </option>
                ))}
              </select>

              <select
                value={category}
                onChange={(event) =>
                  setCategory(event.target.value as '' | NotificationCategory)
                }
                className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">全部分类</option>
                {ALL_NOTIFICATION_CATEGORIES.map((value) => (
                  <option key={value} value={value}>
                    {NOTIFICATION_CATEGORY_LABELS[value]}
                  </option>
                ))}
              </select>

              <button
                onClick={() => {
                  void reload();
                }}
                className="h-9 px-3 rounded-md border border-gray-300 text-sm hover:bg-gray-50 transition-colors"
              >
                刷新
              </button>
            </div>
          </div>

          {error && (
            <div className="px-5 py-6 text-sm text-red-500 border-b border-gray-100">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">当前筛选下暂无通知</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      标题
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      分类
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      摘要
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      更新时间
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const active = editingId === item.id;
                    return (
                      <tr
                        key={item.id}
                        className={`border-b border-gray-200 transition-colors ${
                          active ? 'bg-primary/5' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-4 py-3 align-top">
                          <button
                            onClick={() => startEdit(item)}
                            className="text-left"
                          >
                            <div className="font-medium text-gray-900">{item.title}</div>
                            <div className="mt-1 text-xs text-gray-400">
                              创建于 {formatDateTime(item.created_at)}
                            </div>
                          </button>
                        </td>
                        <td className="px-4 py-3 align-top text-center">
                          <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                            {item.market_id || 'CN'} · {NOTIFICATION_CATEGORY_LABELS[item.category]}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top text-gray-600 max-w-[280px]">
                          <div>{item.summary}</div>
                        </td>
                        <td className="px-4 py-3 align-top text-center text-xs text-gray-500 whitespace-nowrap">
                          {formatDateTime(item.updated_at)}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={() => startEdit(item)}
                              className="text-primary hover:underline"
                            >
                              编辑
                            </button>
                            <button
                              onClick={async () => {
                                const confirmed = window.confirm(
                                  `确认删除「${item.title}」吗？删除后移动端将不再展示该通知。`,
                                );
                                if (!confirmed) {
                                  return;
                                }
                                const ok = await remove(item.id);
                                if (ok && editingId === item.id) {
                                  startCreate();
                                }
                              }}
                              className="text-red-500 hover:underline"
                            >
                              删除
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 text-sm text-gray-500">
            <span>第 {page} / {totalPages} 页</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1 || loading}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                上一页
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages || loading}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                下一页
              </button>
            </div>
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-xl p-5 xl:sticky xl:top-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-medium text-gray-900">
                {editingItem ? '编辑通知' : '新建通知'}
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                {editingItem
                  ? `当前编辑 ID ${editingItem.id}，保存后移动端会读取最新内容。`
                  : '创建后会进入真实通知库，移动端将从接口读取。'}
              </p>
            </div>
            {editingItem ? (
              <button
                onClick={startCreate}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                切换为新建
              </button>
            ) : null}
          </div>

          <form className="mt-5 space-y-4" onSubmit={(event) => void handleSubmit(event)}>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">所属地区</span>
              <select
                value={form.marketId}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, marketId: event.target.value }))
                }
                className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {markets.map((market) => (
                  <option key={market.market_id} value={market.market_id}>
                    {market.market_id} · {market.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">消息分类</span>
              <select
                value={form.category}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    category: event.target.value as NotificationCategory,
                  }))
                }
                className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {ALL_NOTIFICATION_CATEGORIES.map((value) => (
                  <option key={value} value={value}>
                    {NOTIFICATION_CATEGORY_LABELS[value]}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">标题</span>
              <input
                value={form.title}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, title: event.target.value }))
                }
                maxLength={80}
                className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="例如：你的任务审核已通过"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">摘要</span>
              <input
                value={form.summary}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, summary: event.target.value }))
                }
                maxLength={120}
                className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="列表中展示的简要说明"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">正文</span>
              <textarea
                value={form.body}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, body: event.target.value }))
                }
                rows={6}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="消息详情正文，供移动端详情展示或后续扩展使用"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">动作文案</span>
              <input
                value={form.actionLabel}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, actionLabel: event.target.value }))
                }
                maxLength={30}
                className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="可选，例如：立即查看"
              />
            </label>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 rounded-md bg-primary text-white text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {saving ? '保存中...' : editingItem ? '保存修改' : '发布通知'}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setForm(editingItem ? mapForm(editingItem) : EMPTY_FORM);
                }}
                className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                重置
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
